import cn from 'classnames';
import React, { memo } from 'react';

import { Badge, Card, Space, Typography } from '@sravni/react-design-system';

import { UNBREAKABLE_SEPARATOR } from '@src/constants/unbreakableSeparator';
import { formatMoney } from '@src/helpers/formatMoney';

import { DAYS_IN_WEEK } from '../../config';
import { formatDateWithOffset } from '../../lib';

import {
  LONG_AMOUNT_COEFFICENT_CHANGE,
  LONG_TERM_FIRST_RANGE_COEFFICIENT,
  LONG_TERM_SECOND_RANGE_COEFFICIENT,
} from './FullDealEntryPointFormCalculations.config';
import styles from './FullDealEntryPointFormCalculations.module.scss';
import { calculateLongDebt, calculateShortDebt } from './lib';

interface Props {
  term: number;
  amount: number;
}

export const FullDealEntryPointFormCalculations = memo(({ term, amount }: Props) => {
  const withoutOverpayment = term < 22;
  const isShowShortDebt = term < 34;

  const amountWithOverpayment = calculateShortDebt(amount, term);
  const repaymentDate = formatDateWithOffset(term - 1);

  if (isShowShortDebt) {
    return (
      <Card className={styles.container} color="dark" size={16}>
        <Space className={styles.content}>
          <Typography.Text size={12}>К возврату</Typography.Text>

          <Space size={4} justify="start" align="center">
            {withoutOverpayment && (
              <Badge
                className={styles.badge}
                color="green"
                variant="primary"
                text={<Typography.Text size={14}>0%</Typography.Text>}
              />
            )}

            <Typography.Text
              size={14}
              className={cn({
                'h-color-G100': withoutOverpayment,
              })}
              strong
            >
              {withoutOverpayment ? formatMoney(amount) : formatMoney(amountWithOverpayment)}
            </Typography.Text>

            {withoutOverpayment && (
              <Typography.Text size={14} className={styles.decoratedText}>
                {formatMoney(amountWithOverpayment, { withSymbol: false })}
              </Typography.Text>
            )}
          </Space>
        </Space>

        <Space className={styles.rightContent}>
          <Typography.Text size={12}>Погасить до</Typography.Text>
          <Typography.Text size={14} strong className={styles.alignRight}>
            {repaymentDate}
          </Typography.Text>
        </Space>
      </Card>
    );
  }

  const termByWeeks = Math.round(term / DAYS_IN_WEEK);
  const calculations = calculateLongDebt(
    amount,
    termByWeeks,
    amount > LONG_AMOUNT_COEFFICENT_CHANGE ? LONG_TERM_SECOND_RANGE_COEFFICIENT : LONG_TERM_FIRST_RANGE_COEFFICIENT,
  );
  // @todo: Пока не удаляем, скорее всего потом пригодится
  // termByWeeks > TERM_SLIDER_MIN_THIRD_RANGE && termByWeeks < TERM_SLIDER_MAX_THIRD_RANGE
  //   ? calculateLongDebt(amount, termByWeeks, LONG_TERM_FIRST_RANGE_COEFFICIENT)
  //   : calculateLongDebt(amount, termByWeeks, LONG_TERM_SECOND_RANGE_COEFFICIENT);

  return (
    <Card className={styles.longDebtContainer} color="dark" size={16}>
      <Space className={styles.content}>
        <Typography.Text size={12}>Платеж раз в 2 недели</Typography.Text>
        <Typography.Text size={14} className={styles.alignRight}>
          <Typography.Text as="span" strong>
            {formatMoney(calculations)}
          </Typography.Text>
          {UNBREAKABLE_SEPARATOR}
          <Typography.Text as="span" size={10} className="h-color-D40">
            c {formatDateWithOffset(14, false)}
          </Typography.Text>
        </Typography.Text>
      </Space>

      <Space className={styles.content}>
        <Typography.Text size={12}>Погасить до</Typography.Text>
        <Typography.Text size={14} strong className={styles.alignRight}>
          {repaymentDate}
        </Typography.Text>
      </Space>
    </Card>
  );
});

FullDealEntryPointFormCalculations.displayName = 'FullDealEntryPointFormCalculations';
